import axios from 'axios';
import fetchServerPublicKey from './serverPublicKey';
import refreshToken from './refreshToken';
import { v4 as uuidv4 } from 'uuid';
import { Buffer } from 'buffer';
import { Token, LoggedInUser } from './Token';
import * as Storage from './Storage';

const operations = window.crypto.subtle || window.crypto.webkitSubtle;
let serverPublicKey, clientPublicKey, clientPrivateKey, serverPublicKeyHash;
const toBase64 = arrayBuffer => {
  return Buffer.from(arrayBuffer, 'utf-8').toString('base64');
};
const decryptAESKey = async encryptedAESKeyBase64 => {
  try {
    // تبدیل base64 به ArrayBuffer
    const encryptedAesKeyBuffer = Uint8Array.from(atob(encryptedAESKeyBase64), c => c.charCodeAt(0)).buffer;
    const aesKeyBuffer = await operations.decrypt({
      name: "RSA-OAEP",
      hash: {
        name: "SHA-256"
      }
    }, clientPrivateKey, encryptedAesKeyBuffer);
    return new Uint8Array(aesKeyBuffer);
  } catch (error) {
    console.error("Decryption failed:", error);
    throw error; // برای بررسی خطاهای بیشتر
  }
};
function base64ToUint8Array(base64) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}
async function decryptData(encryptedDataBase64, aesKeyBuffer, iv, url) {
  try {
    const aesKey = await operations.importKey("raw",
    // فرمت کلید AES
    aesKeyBuffer,
    // داده کلید AES به صورت ArrayBuffer
    {
      name: "AES-CBC"
    },
    // الگوریتم AES-CBC
    false,
    // عدم استخراج کلید
    ["decrypt"] // عملیات مجاز (در اینجا فقط رمزگشایی)
    );
    const ivBuffer = base64ToUint8Array(iv);
    const encryptedData = base64ToUint8Array(encryptedDataBase64);
    const decryptedData = await operations.decrypt({
      name: "AES-CBC",
      // استفاده از الگوریتم AES-CBC
      iv: ivBuffer // آی وی که برای رمزگشایی استفاده می‌شود
    }, aesKey,
    // کلید AES
    encryptedData // داده‌های رمزنگاری‌شده
    );
    // تبدیل داده‌های رمزگشایی‌شده به رشته
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.log(error);
  }
}
const generateClientKeyPair = async () => {
  const keyPair = await operations.generateKey({
    name: "RSA-OAEP",
    // استفاده از الگوریتم RSA-OAEP برای رمزنگاری
    modulusLength: 2048,
    // طول کلید (2048 بیت برای امنیت بیشتر)
    publicExponent: new Uint8Array([1, 0, 1]),
    // مقدار عمومی برای RSA
    hash: {
      name: "SHA-256"
    } // الگوریتم هش برای RSA
  }, true,
  // کلیدها باید استفاده‌شدنی باشند
  ["encrypt", "decrypt"] // عملیات‌هایی که این کلیدها می‌تونند انجام بدن
  );
  clientPublicKey = keyPair.publicKey;
  clientPrivateKey = keyPair.privateKey;
};
const exportClientPublicKeyToPem = async publicKey => {
  // استخراج کلید عمومی به فرمت DER
  const exportedKey = await operations.exportKey("spki",
  // فرمت SPKI برای کلید عمومی
  publicKey);
  // تبدیل کلید DER به Base64
  const exportedAsString = String.fromCharCode(...new Uint8Array(exportedKey));
  const exportedAsBase64 = btoa(exportedAsString);
  // افزودن Header و Footer برای فرمت PEM
  return `-----BEGIN PUBLIC KEY-----\n${exportedAsBase64}\n-----END PUBLIC KEY-----`;
};
const encryptData = async (message, serverPublicKey) => {
  // 1. تولید کلید متقارن (AES)
  const aesKey = await operations.generateKey({
    name: "AES-CBC",
    length: 256
  }, true, ["encrypt", "decrypt"]);

  // 2. رمزنگاری پیام با AES
  const encoder = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(16)); // بردار اولیه
  const encryptedMessage = await operations.encrypt({
    name: "AES-CBC",
    iv: iv
  }, aesKey, encoder.encode(message));

  // 3. استخراج کلید AES به صورت خام
  const aesKeyRaw = await operations.exportKey("raw", aesKey);
  const encryptedMessageBase64 = toBase64(encryptedMessage);
  const ivBase64 = toBase64(iv);
  return {
    encryptedMessageBase64,
    aesKeyRaw,
    ivBase64
  };
};
const encryptAESKey = async (aesKeyRaw, serverPublicKeyPem) => {
  // تبدیل کلید عمومی PEM به CryptoKey
  const serverPublicKey = await operations.importKey("spki", decodeBase64(serverPublicKeyPem), {
    name: "RSA-OAEP",
    hash: "SHA-256"
  }, true, ["encrypt"]);

  // رمزنگاری کلید AES با کلید عمومی RSA
  const encryptedAesKey = await operations.encrypt({
    name: "RSA-OAEP"
  }, serverPublicKey, aesKeyRaw);
  const encryptedAesKeyBase64 = toBase64(encryptedAesKey);
  return encryptedAesKeyBase64;
};

// تابع کمکی برای دیکد کردن PEM
const decodeBase64 = (pem) => {
  const pemContents = pem.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\n/g, '')
  const binaryString = atob(pemContents);
  const binaryArray = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    binaryArray[i] = binaryString.charCodeAt(i);
  }
  return binaryArray.buffer;
};
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_SERVER}/api/${uuidv4().replace(/-/g, '')}/`,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Requests before sending
axiosInstance.interceptors.request.use(async config => {
  const accessToken = await Token();
  const loggedInUser = await LoggedInUser();
  try {
    await generateClientKeyPair();
    const clientPublicKeyPem = await exportClientPublicKeyToPem(clientPublicKey);
    let serverPublicKeyData = JSON.parse(sessionStorage.getItem('serverPublicKeyData'));
    if (!serverPublicKeyData) {
      await fetchServerPublicKey();
      serverPublicKeyData = JSON.parse(sessionStorage.getItem('serverPublicKeyData'));
    }
    serverPublicKey = serverPublicKeyData.publicKey;
    serverPublicKeyHash = serverPublicKeyData.publicKeyHash;
    config.headers['Authorization'] = accessToken ? `Bearer ${accessToken}` : '';
    config.headers['server-public-key-hash'] = serverPublicKeyHash;
    config.headers['x-current-organization'] = loggedInUser ? loggedInUser.currentOrganization : '';
    config.headers['client-public-key'] = Buffer.from(clientPublicKeyPem, 'utf-8').toString('base64');
    if (config.data) {
      const data = JSON.stringify(config.data);
      const {
        encryptedMessageBase64,
        aesKeyRaw,
        ivBase64
      } = await encryptData(data, serverPublicKey);
      const encryptedAesKeyBase64 = await encryptAESKey(aesKeyRaw, serverPublicKey);
      config.data = {
        encryptedData: {
          encryptedMessage: encryptedMessageBase64,
          encryptedAesKey: encryptedAesKeyBase64,
          iv: ivBase64
        }
      };
    }
    return config;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
}, error => Promise.reject(error));
//

const processDecryption = async (encryptedData, encryptedAESKey, ivBase64, url) => {
  try {
    // رمزگشایی AES Key
    const aesKey = await decryptAESKey(encryptedAESKey);
    // تبدیل iv از base64 به Uint8Array
    const iv = ivBase64;

    // رمزگشایی داده‌ها با کلید AES
    const decryptedData = await decryptData(encryptedData, aesKey, iv, url);
    return decryptedData;
  } catch (error) {
    console.log(error);
  }
};
axiosInstance.interceptors.response.use(async response => {
  try {
    if (response.data && response.data.encryptedData) {
      const encryptedData = response.data.encryptedData;
      const encryptedAESKey = response.data.encryptedAESKey;
      const ivBase64 = response.data.iv;
      const decryptedData = await processDecryption(encryptedData, encryptedAESKey, ivBase64, response.config.url);
      response.data = JSON.parse(decryptedData);
    }
    return response;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
}, error => {
  return Promise.reject(error);
});


let isRequestInProgress = false;  // قفل برای جلوگیری از درخواست همزمان

async function waitForRequestQueue(requestId) {
  return new Promise((resolve) => {
    const checkQueue = () => {
      const storedRequest = JSON.parse(sessionStorage.getItem("storedRequest"));
      if (storedRequest && storedRequest.length) {
        if (requestId.toString() === storedRequest[0].toString()) {
          // درخواست تکمیل شده است
          storedRequest.shift();
          sessionStorage.setItem("storedRequest", JSON.stringify(storedRequest));
          resolve();  // درخواست بعدی می‌تواند اجرا شود
        } else {
          // درخواست هنوز به صف نرسیده است
          setTimeout(checkQueue, 300);  // دوباره چک می‌کنیم
        }
      } else {
        resolve();  // صف خالی است، می‌توان درخواست را ارسال کرد
      }
    };
    checkQueue();
  });
}


const Axios = async (request, retry = 0) => {
  request._id = uuidv4()
  let storedRequest = JSON.parse(sessionStorage.getItem("storedRequest")) || [];
  storedRequest.push(request._id)
  sessionStorage.setItem("storedRequest", JSON.stringify(storedRequest));
  let response;
  if (retry < 3) {
    retry++;
    try {
      await waitForRequestQueue(request._id);

       // قفل را فعال می‌کنیم
       if (isRequestInProgress) {
        // اگر یک درخواست در حال انجام باشد، منتظر می‌مانیم
        await new Promise(resolve => {
          const interval = setInterval(() => {
            if (!isRequestInProgress) {
              clearInterval(interval);
              resolve();
            }
          }, 300); // هر 300 میلی‌ثانیه بررسی می‌کنیم
        });
      }

      isRequestInProgress = true;  // فعال کردن قفل

      response = await axiosInstance.request(request, retry);
      if(response.data.status === 404) {
        isRequestInProgress = false;
      }
      if (response.data.status === 4002 && retry < 3) {
        isRequestInProgress = false
        sessionStorage.removeItem("serverPublicKeyData");
        response = await Axios(request, retry);
      }
      if (response.data.status === 4003 && retry < 3) {
        isRequestInProgress = false
        const retryResponse = await refreshToken();
        if (retryResponse) {
          if (retryResponse.data.status === 201) {
            await Storage.remove('accessToken');
            await Storage.remove('loggedInUser');
            await Storage.remove('userOptions');
            await Storage.remove('sessionId');
            window.location = '/';
          } else {
            response = await Axios(request, retry);
          }
        } else {
          response = await Axios(request, retry);
        }
      }
    } catch (error) {
      isRequestInProgress = false;
      response = error.response;
    }
    finally {
      // بعد از اتمام درخواست، قفل را آزاد می‌کنیم
      isRequestInProgress = false;
    }
  }
  return response;
};
export default Axios;